import { EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { monoFont, tabularNums } from '@medcom/shared-ui/styles/globalCss.ts';
import { App, Button, Flex, Modal, Space, Table, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { LabelImage } from '../../../components/LabelImage.tsx';
import { LoadingIndicator } from '../../../components/LoadingIndicator.tsx';
import { TableList } from '../../../components/TableList.tsx';
import { TestTubeColor } from '../../../components/TestTubeColor.tsx';
import { LocalizationFieldType } from '../../../graphql/generated/graphql.ts';
import { useObjectUrlResource } from '../../../hooks/useObjectUrlResource.ts';
import { printErrorShown, usePrinter } from '../../../hooks/usePrinter.ts';
import { PrintButton } from '../../../PrintButton.tsx';
import { fieldsTableHead } from '../../../styles/fieldsTable.ts';
import { tableActionCell } from '../../../styles/globalCss.ts';
import { SHORT_DATETIME_FORMAT } from '../../../utils/dateFormatUtils.ts';
import { testTubeColorProperties, translateSpecimenFeature } from '../../../utils/enumHelpers.ts';
import { germanDecimal } from '../../../utils/localizationValue.ts';
import { NOT_AVAILABLE } from '../../../utils/placeholders.ts';
import { useMarkOrderLabelPrintedMutation } from './useMarkOrderLabelPrintedMutation.tsx';
import type { Order, OrderLabel } from './useRequestDetailsQuery.tsx';

export const LabelsTab = ({ order, requestId, onPrinted }: { order: Order; requestId: string; onPrinted: () => void }) => {
  const { message } = App.useApp();
  const { printLabels } = usePrinter();

  const [markOrderLabelPrintedMutation] = useMarkOrderLabelPrintedMutation();
  const { token } = theme.useToken();
  const {
    objectUrl: previewUrl,
    fetchAndSet: fetchPreview,
    cleanup: cleanupPreview,
    loading: previewLoading,
  } = useObjectUrlResource(() => message.error('Die Etiketten-Vorschau konnte nicht geladen werden'), 'image/png');

  const materialStyle = css`
    display: inline-flex;
    align-items: center;
    gap: ${token.marginXS}px;
    font-weight: 500;
  `;

  const locationStyle = css`
    display: inline-block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  `;

  /** The whole row opens the detail, so it has to look like it can be clicked. */
  const tableStyle = css`
    .ant-table-row {
      cursor: pointer;
    }
  `;

  /** The state someone opens this tab for, so it is worth a word rather than an empty cell. */
  const unprintedStyle = css`
    color: ${token.colorTextTertiary};
    font-style: italic;
  `;

  // a reprint prints every given label, printed or not, then marks each one printed again, as the desktop client does
  const printOrderLabels = async (orderLabels: OrderLabel[]) => {
    const complete = await printLabels(orderLabels, { requestId, orderId: order.id }).catch(e => (printErrorShown(e) ? null : false));
    if (complete === null) {
      return;
    }
    if (!complete) {
      message.error('Beim Drucken ist ein Fehler aufgetreten');
      return;
    }

    message.success('Druckauftrag erstellt');

    try {
      for (const label of orderLabels) {
        await markOrderLabelPrintedMutation({
          variables: {
            orderLabelId: label.id,
          },
        });
      }

      onPrinted();
    } catch (_e) {
      message.error('Der Druckstatus kann nicht gesetzt werden');
    }
  };

  /**
   * Six columns, because fifteen did not fit the modal and only these answer the question this tab
   * is opened with: which tube, which barcode, for what, and has it been printed. Everything else
   * is a row deeper.
   */
  const labelColumns: ColumnsType<OrderLabel> = [
    {
      title: 'Material',
      dataIndex: 'name',
      key: 'name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.name.localeCompare(b.name),
      // The cap colour is what you reach for at the bench; as a word it costs a column of its own.
      render: (value, record) => (
        <span className={materialStyle}>
          <TestTubeColor properties={testTubeColorProperties(record.color)} withText={false} />
          {value}
        </span>
      ),
    },
    {
      title: 'Nummer',
      dataIndex: 'number',
      key: 'number',
      sorter: (a, b) => a.number.localeCompare(b.number),
      render: v => <span className={monoFont}>{v}</span>,
    },
    {
      title: 'Parameter',
      dataIndex: 'params',
      key: 'params',
      render: (_, record) => <TableList entries={record.paramShortNames} maxEntries={3} withinModal mono />,
    },
    {
      title: 'Lokalisation',
      dataIndex: 'location',
      key: 'location',
      sorter: (a, b) => a.location.localeCompare(b.location),
      // the Zusatzfelder are one row-click away, so the column stays the name alone
      // free text is the one thing that can push the fixed action column over the other cells
      render: value =>
        value ? (
          <span className={locationStyle} title={value}>
            {value}
          </span>
        ) : (
          <Typography.Text type="secondary">{NOT_AVAILABLE}</Typography.Text>
        ),
    },
    {
      title: 'Letzter Druck',
      dataIndex: 'printedAt',
      key: 'printedAt',
      sorter: (a, b) => (a.printedAt ?? '').localeCompare(b.printedAt ?? ''),
      render: value =>
        value ? (
          <span className={tabularNums}>{dayjs(value).format(SHORT_DATETIME_FORMAT)}</span>
        ) : (
          <span className={unprintedStyle}>noch nicht gedruckt</span>
        ),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      align: 'right',
      className: tableActionCell,
      render: (_, record) => (
        <>
          <Button
            icon={<EyeOutlined />}
            type="link"
            aria-label="Vorschau"
            onClick={e => {
              e.stopPropagation();
              fetchPreview(`${window._env_.API_URL}/rest/order-label/${record.id}/preview`);
            }}
          />
          <PrintButton
            icon={<PrinterOutlined />}
            type="link"
            aria-label="Drucken"
            onClick={e => {
              e.stopPropagation();
              return printOrderLabels([record]);
            }}
          />
        </>
      ),
    },
  ];

  return order.orderLabels.length ? (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <PrintButton icon={<PrinterOutlined />} onClick={() => printOrderLabels(order.orderLabels)}>
        Alle Etiketten drucken
      </PrintButton>
      <Table<OrderLabel>
        className={tableStyle}
        // columns as wide as their content; a narrow modal scrolls sideways instead of wrapping the cells
        tableLayout="auto"
        scroll={{ x: 'max-content', y: '300px' }}
        rowKey={label => label.id}
        size="small"
        showSorterTooltip={false}
        dataSource={order.orderLabels}
        pagination={false}
        columns={labelColumns}
        expandable={{
          expandedRowRender: label => <LabelDetail label={label} />,
          expandRowByClick: true,
          // the default leaves the toggle floating a long way from the first column
          columnWidth: 38,
        }}
      />
      <Modal open={!!previewUrl || previewLoading} onCancel={cleanupPreview} footer={null} centered title="Etikettenvorschau">
        <Flex justify="center">{previewLoading ? <LoadingIndicator /> : <LabelImage src={previewUrl} scale={1} />}</Flex>
      </Modal>
    </Space>
  ) : (
    <Typography.Text type="secondary">Es werden keine Etiketten benötigt</Typography.Text>
  );
};

/** Everything the six columns leave out - the fields that are looked up rather than scanned. */
const LabelDetail = ({ label }: { label: OrderLabel }) => {
  const { token } = theme.useToken();

  const containerStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${token.marginSM}px;
  `;

  const blockLabelStyle = css`
    font-size: ${token.fontSizeSM}px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${token.colorTextTertiary};
    margin-bottom: ${token.marginXXS}px;
  `;

  /** Same shape the Anforderung's summary uses: the field names once, in a header. */
  const fieldsStyle = css`
    /* antd styles every table inside its wrapper as ".ant-table-wrapper table", which outranks a
       single class - so this one has to be doubled to win. Without it the two fields are spread
       across the whole row and the header rule disappears under border-collapse: separate. */
    && {
      width: auto;
      table-layout: auto;
      border-collapse: collapse;
    }

    font-size: ${token.fontSize}px;


    td {
      padding: ${token.paddingXS}px ${token.marginLG}px 0 0;
      vertical-align: baseline;
      font-weight: 500;
    }

    th:last-child,
    td:last-child {
      padding-right: 0;
    }
  `;

  const emptyStyle = css`
    color: ${token.colorTextTertiary};
    font-weight: 400;
  `;

  const restStyle = css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: ${token.marginXXS}px ${token.marginLG}px;
  `;

  /** A UUID is 36 characters and wraps to three lines in a grid cell, which breaks the rhythm of
   *  every row beside it. It gets the width of the whole block instead. */
  const wideEntryStyle = css`
    grid-column: 1 / -1;
  `;

  const entryStyle = css`
    display: flex;
    gap: ${token.marginXS}px;
    font-size: ${token.fontSizeSM}px;
    min-width: 0;
  `;

  const termStyle = css`
    flex: none;
    width: 120px;
    color: ${token.colorTextTertiary};
  `;

  const valueStyle = css`
    min-width: 0;
    overflow-wrap: anywhere;
  `;

  const dividerStyle = css`
    height: 1px;
    background: ${token.colorSplit};
  `;

  const entry = (term: string, value: React.ReactNode, wide = false) => (
    <div className={`${entryStyle} ${wide ? wideEntryStyle : ''}`}>
      <span className={termStyle}>{term}</span>
      <span className={valueStyle}>{value}</span>
    </div>
  );

  const orDash = (value: string) => (value ? value : <span className={emptyStyle}>{NOT_AVAILABLE}</span>);
  const fields = label.localizationFieldValues;

  return (
    <div className={containerStyle}>
      {/* a label without a Lokalisation gets no empty table, just the rest */}
      {!!fields.length && (
        <div>
          <div className={blockLabelStyle}>Zusatzfelder der Lokalisation</div>
          <table className={cx(fieldsStyle, fieldsTableHead(token))}>
            <thead>
              <tr>
                {fields.map(field => (
                  <th key={field.label}>{field.unit ? `${field.label} (${field.unit})` : field.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {fields.map(field => (
                  <td key={field.label} className={field.inputType === LocalizationFieldType.NUMBER ? tabularNums : undefined}>
                    {germanDecimal(field.value, field.inputType)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!!fields.length && <div className={dividerStyle} />}

      <div className={restStyle}>
        {entry('Kennung', <span className={monoFont}>{orDash(label.classification)}</span>)}
        {entry('Laufnummer', <span className={tabularNums}>{label.runningNumber}</span>)}
        {entry('Text', orDash(label.text))}
        {entry('Text 2', orDash(label.text2))}
        {entry('Eigenschaften', orDash(label.features.map(translateSpecimenFeature).join(', ')))}
        {entry('Analysestationen', orDash(label.analysisStationNames.join(', ')))}
        {entry('Spezialdrucker', label.specialPrinterRequired ? 'ja' : <span className={emptyStyle}>nein</span>)}
        {window._env_.ENV_TAG !== 'prod' &&
          entry(
            'UUID',
            <Typography.Text copyable code>
              {label.id}
            </Typography.Text>,
            true
          )}
      </div>
    </div>
  );
};
